/**
 * FormRunner — a generic multi-step form driven by a FormDefinition config.
 *
 * Powers the "quote" section for service clients. Features:
 *   • Multi-step wizard (one FormStep per screen)
 *   • Conditional field visibility via visibleWhen
 *   • Running price estimate from pricingImpact (fixed / per_unit / percentage)
 *   • On submit writes a QuoteSubmission to clients/{clientId}/quotes
 */
import React, { useState, useMemo, useCallback } from "react";
import { collection, addDoc } from "firebase/firestore";
import { getDb } from "@repo/backend/firebase";
import { logConversionEvent } from "@repo/backend/firebase/analytics";
import { submitQuoteLead } from "@repo/backend";
import type { FormDefinition, FormField, FormStep } from "../schema/formDef.js";
import type { QuoteSubmission } from "../schema/formDef.js";

// ── Types ─────────────────────────────────────────────────────────────────────

type FieldValue = string | number | boolean | string[];
type FormValues = Record<string, FieldValue>;

export interface FormRunnerProps {
  /** FormDefinition config — passed from section props or fetched from Firestore */
  form: FormDefinition;
  clientId: string;
  /** Optional override for the submit button label */
  submitLabel?: string;
  /**
   * When set, calls this Firebase callable instead of writing to Firestore.
   * Currently supported: "submitQuoteLead" (Jobber-backed clients).
   */
  submitCallable?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isVisible(field: FormField, values: FormValues): boolean {
  if (!field.visibleWhen) return true;
  const { fieldId, operator, value: condValue } = field.visibleWhen;
  const fv = values[fieldId];
  switch (operator) {
    case "eq":  return fv === condValue;
    case "neq": return fv !== condValue;
    case "gt":  return typeof fv === "number" && typeof condValue === "number" && fv > condValue;
    case "lt":  return typeof fv === "number" && typeof condValue === "number" && fv < condValue;
    case "contains":
      if (Array.isArray(fv)) return fv.includes(String(condValue));
      return String(fv ?? "").includes(String(condValue));
    default: return true;
  }
}

/** Compute total estimate in cents from all visible pricingImpact fields */
function computeEstimate(step: FormStep, values: FormValues): number {
  let total = 0;
  for (const field of step.fields) {
    if (!field.pricingImpact || !isVisible(field, values)) continue;
    const { type, amountCents, unitField } = field.pricingImpact;
    const fieldVal = values[field.id];
    if (type === "fixed" && fieldVal) {
      total += amountCents;
    } else if (type === "per_unit") {
      const multiplier = unitField
        ? Number(values[unitField] ?? 1)
        : typeof fieldVal === "number" ? fieldVal : Number(fieldVal ?? 1);
      total += amountCents * Math.max(1, multiplier);
    } else if (type === "percentage" && fieldVal) {
      // percentage is applied to the running total so far
      total += Math.round(total * (amountCents / 10000));
    }
  }
  return total;
}

/** Sum estimates across all steps */
function computeTotalEstimate(steps: FormStep[], values: FormValues): number {
  return steps.reduce((acc, step) => acc + computeEstimate(step, values), 0);
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const INPUT_CLS =
  "w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow";

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface FieldRendererProps {
  field: FormField;
  values: FormValues;
  onChange: (id: string, val: FieldValue) => void;
}

function FieldRenderer({ field, values, onChange }: FieldRendererProps) {
  if (!isVisible(field, values)) return null;

  const value = values[field.id] ?? "";

  const label = (
    <label className="block text-sm font-medium text-foreground mb-1.5">
      {field.label}
      {field.required && <span className="text-destructive ml-1" aria-hidden>*</span>}
    </label>
  );

  switch (field.type) {
    case "text":
    case "email":
    case "phone":
      return (
        <div>
          {label}
          <input
            type={field.type === "phone" ? "tel" : field.type}
            value={String(value)}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className={INPUT_CLS}
          />
        </div>
      );

    case "number":
      return (
        <div>
          {label}
          <input
            type="number"
            value={String(value)}
            onChange={(e) => onChange(field.id, Number(e.target.value))}
            placeholder={field.placeholder}
            required={field.required}
            className={INPUT_CLS}
          />
        </div>
      );

    case "textarea":
      return (
        <div>
          {label}
          <textarea
            value={String(value)}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={3}
            className={INPUT_CLS + " resize-none"}
          />
        </div>
      );

    case "select":
      return (
        <div>
          {label}
          <select
            value={String(value)}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            className={INPUT_CLS}
          >
            <option value="">{field.placeholder ?? "Select…"}</option>
            {field.options?.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );

    case "multiselect": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-2">
            {field.options?.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    const next = on ? selected.filter((v) => v !== o.value) : [...selected, o.value];
                    onChange(field.id, next);
                  }}
                  className={[
                    "rounded-full border px-4 py-1.5 text-sm font-medium transition-all",
                    on
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-foreground hover:bg-muted/60",
                  ].join(" ")}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    case "checkbox":
      return (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(field.id, e.target.checked)}
            className="mt-0.5 rounded border-border text-primary focus:ring-primary/40"
          />
          <span className="text-sm text-foreground">{field.label}</span>
        </label>
      );

    case "date":
      return (
        <div>
          {label}
          <input
            type="date"
            value={String(value)}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            className={INPUT_CLS}
          />
        </div>
      );

    case "time":
      return (
        <div>
          {label}
          <input
            type="time"
            value={String(value)}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            className={INPUT_CLS}
          />
        </div>
      );

    default:
      return null;
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export function FormRunner({ form, clientId, submitLabel, submitCallable }: FormRunnerProps) {
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState<FormValues>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const step = form.steps[stepIdx];
  const isLast = stepIdx === form.steps.length - 1;

  const estimate = useMemo(() => computeTotalEstimate(form.steps, values), [form.steps, values]);
  const hasEstimate = estimate > 0;

  const handleChange = useCallback((id: string, val: FieldValue) => {
    setValues((prev) => ({ ...prev, [id]: val }));
  }, []);

  function handleNext(e: React.FormEvent) {
    e.preventDefault();
    if (!isLast) {
      setStepIdx((i) => i + 1);
      return;
    }
    handleSubmit();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError("");
    try {
      const customerName = String(values["name"] ?? values["customerName"] ?? "");
      const customerEmail = String(values["email"] ?? values["customerEmail"] ?? "");
      const customerPhone = String(values["phone"] ?? values["customerPhone"] ?? "") || undefined;

      if (submitCallable === "submitQuoteLead") {
        const allFields = form.steps.flatMap((s) => s.fields);
        await submitQuoteLead({
          clientId,
          customerName,
          customerEmail,
          customerPhone,
          subject: form.title || "Free Estimate Request",
          fields: allFields
            .filter((f) => isVisible(f, values) && values[f.id] !== undefined && String(values[f.id]) !== "")
            .map((f) => ({ label: f.label, value: String(values[f.id]) })),
          confirmationMessage: form.successMessage,
        });
      } else {
        const now = new Date().toISOString();
        const submission: Omit<QuoteSubmission, "id"> = {
          clientId,
          formId: form.id,
          status: "new",
          fields: values,
          estimateCents: estimate,
          customerName,
          customerEmail,
          customerPhone,
          notes: String(values["notes"] ?? "") || undefined,
          createdAt: now,
          updatedAt: now,
        };
        await addDoc(collection(getDb(), "clients", clientId, "quotes"), submission);
      }
      logConversionEvent("generate_lead", {
        form_type: "quote",
        ...(submitCallable === "submitQuoteLead" ? {} : { value: estimate / 100, currency: "USD" }),
      });
      setDone(true);
    } catch (err) {
      console.error("[FormRunner] submit failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="px-8 py-14 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">Request Received!</h3>
        <p className="text-muted-foreground text-sm mt-2 leading-relaxed max-w-xs mx-auto">
          {form.successMessage}
        </p>
      </div>
    );
  }

  if (!step) return null;

  const visibleFields = step.fields.filter((f) => isVisible(f, values));
  const isComplete = step.fields
    .filter((f) => f.required && isVisible(f, values))
    .every((f) => {
      const v = values[f.id];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== "";
    });

  return (
    <div className="bg-background rounded-2xl border border-border shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)] overflow-hidden">
      {/* Step indicator */}
      {form.steps.length > 1 && (
        <div className="flex items-center justify-center gap-2 pt-7 px-8">
          {form.steps.map((_, i) => (
            <div
              key={i}
              className={[
                "h-1.5 rounded-full transition-all duration-300",
                i === stepIdx ? "w-7 bg-primary" : i < stepIdx ? "w-2 bg-primary/35" : "w-2 bg-border",
              ].join(" ")}
            />
          ))}
        </div>
      )}

      <form onSubmit={handleNext} className="px-8 pt-6 pb-8">
        {/* Back nav */}
        {stepIdx > 0 && (
          <button
            type="button"
            onClick={() => setStepIdx((i) => i - 1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ChevronLeft />
            Back
          </button>
        )}

        {step.title && (
          <p className="text-sm font-semibold text-foreground mb-1">{step.title}</p>
        )}
        {step.description && (
          <p className="text-sm text-muted-foreground mb-4">{step.description}</p>
        )}

        <div className="space-y-4">
          {visibleFields.map((field) => (
            <FieldRenderer key={field.id} field={field} values={values} onChange={handleChange} />
          ))}
        </div>

        {/* Running estimate */}
        {hasEstimate && (
          <div className="mt-5 rounded-xl bg-primary/5 border border-primary/15 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Estimated total</span>
            <span className="text-base font-bold text-primary tabular-nums">{formatCents(estimate)}</span>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={!isComplete || submitting}
          className="mt-6 w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-all disabled:opacity-35 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <><Spinner />{isLast ? "Submitting…" : "Next…"}</>
          ) : isLast ? (submitLabel ?? form.submitLabel) : "Next →"}
        </button>
      </form>
    </div>
  );
}
